"use client";

import { themeColors } from "@/utils/themeColors";
import { ChapterWithUtterance, TranscriptChapter } from "./TranscriptChapter";
import { Utterance } from "@knowt/syncing/types/common";
import { useMediaRemote } from "@vidstack/react";
import { useMediaPlayerContextSelector } from "../hooks/useMediaPlayerContext";
import { useActiveTranscriptSegment } from "../hooks/useActiveTranscriptSegment";
import { TranscriptEnum } from "@knowt/syncing/graphql/schema";

type Props = {
    chapters: ChapterWithUtterance[];
    utterances: Utterance;
    containerStyle?: React.CSSProperties;
    transcriptType: TranscriptEnum;
};

export const MediaTranscriptions = ({ chapters, utterances, containerStyle, transcriptType }: Props) => {
    const mediaPlayerRef = useMediaPlayerContextSelector(state => state.mediaPlayerRef);
    const currentTime = useMediaPlayerContextSelector(state => state.currentTime);
    const activeSegment = useActiveTranscriptSegment({ utterances, currentTime });

    const remote = useMediaRemote(mediaPlayerRef);

    const onWordClick = (utterance?: Utterance[number]) => {
        if (!utterance) return;

        remote.seek(utterance.start);
        remote.play();
        remote.unmute();
    };

    const onTimestampClick = (timestamp: number) => {
        remote.seek(timestamp);
        remote.play();
        remote.unmute();
    };

    return (
        <div
            className="scrollbar"
            style={{
                overflowY: "auto",
                borderRadius: "2rem",
                backgroundColor: themeColors.neutralWhite,
                ...containerStyle,
            }}
            data-testid="auto-id-94a8ce58-3d79-4473-8e16-643e4a9e738e">
            <p
                style={{
                    top: 0,
                    position: "sticky",
                    paddingInline: "2.5rem",
                    paddingBlockStart: "2.5rem",
                    paddingBlockEnd: "1.5rem",
                    backgroundColor: themeColors.neutralWhite,
                }}
                className="bodyBold1"
                data-testid="auto-id-448e8553-ddef-4933-870f-2cff0a7ceceb">
                Transcript
            </p>
            {chapters.map(chapter => {
                const isActiveSegmentRelated =
                    activeSegment && activeSegment.start >= chapter.start && activeSegment.start < chapter.end;

                return (
                    <TranscriptChapter
                        key={chapter.start}
                        chapter={chapter}
                        activeSegment={isActiveSegmentRelated ? activeSegment : undefined}
                        onWordClick={onWordClick}
                        onTimestampClick={onTimestampClick}
                        transcriptType={transcriptType}
                    />
                );
            })}
        </div>
    );
};
